import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { RsvpResponseStatsPoint } from '../../../../types/dashboard';
import { ADMIN_STAT_CHART_MAX_BAR_SIZE } from '../../reportChartConfig';

type RsvpsRespondedReportGraphProps = {
    points: RsvpResponseStatsPoint[];
};

const RsvpsRespondedTooltip = ({
    active,
    payload,
    label,
}: TooltipProps<number, string>) => {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload as RsvpResponseStatsPoint;

    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
            }}
        >
            <Typography variant='caption'>{label}</Typography>
            <Typography variant='body2'>
                Accepted: {point.acceptedCount}
            </Typography>
            <Typography variant='body2'>
                Declined: {point.declinedCount}
            </Typography>
            <Typography variant='body2'>
                Pending: {point.pendingCount}
            </Typography>
        </Box>
    );
};

export default function RsvpsRespondedReportGraph({
    points,
}: RsvpsRespondedReportGraphProps) {
    const theme = useTheme();

    if (!points?.length) {
        return (
            <Typography sx={{ mt: 4 }} variant='body2'>
                No RSVP responses found for this selection.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: '100%', height: 420, mt: 3 }}>
            <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                    data={points}
                    margin={{ left: 30 }}
                    maxBarSize={ADMIN_STAT_CHART_MAX_BAR_SIZE}
                >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='label' />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<RsvpsRespondedTooltip />} />
                    <Legend />
                    <Bar
                        dataKey='acceptedCount'
                        fill={theme.palette.green.main}
                        name='Accepted'
                    />
                    <Bar
                        dataKey='declinedCount'
                        fill={theme.palette.red.main}
                        name='Declined'
                    />
                    <Bar
                        dataKey='pendingCount'
                        fill={theme.palette.yellow.main}
                        name='Pending'
                    />
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
